import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ADMIN_DASHBOARD_ROUTE,
  ADMIN_STUDENTS_ROUTE,
  ADMIN_ROOMS_ROUTE,
  ADMIN_STAFF_ROUTE,
  ADMIN_QURAN_REPORT_ROUTE,
  ADMIN_STUDENT_ATTENDANCE_ROUTE,
  ADMIN_STAFF_ATTENDANCE_ROUTE,
  ADMIN_MESSAGES_ROUTE,
  ADMIN_CALENDAR_ROUTE,
  ADMIN_ADMISSION_FORM_ROUTE,
  ADMIN_ADMISSION_FORM_OVERVIEW_ROUTE,
  ADMIN_DOCUMENTS_ROUTE,
} from "../../routes/RoutesPathes";
import { Logo } from "../general/Logo";
import styles from "./Sidebar.module.css";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  RoomIcon,
  StudentIcon,
  StaffIcon,
  ActivityReportIcon,
  AttendanceIcon,
  QuranBlackAndWhiteIcon,
  MessagesIcon,
  CalendarIcon,
} from "../../assets/svg/icons";
import { FiChevronLeft, FiMenu, FiChevronDown, FiChevronRight } from "react-icons/fi";
import { UserMenu } from "../components/js/UserMenu";
import { useInboxContext } from "../../context/InboxContext";
import { BookAIcon, BookIcon, ClipboardList, FolderCheckIcon, FormInputIcon, LinkIcon, TurtleIcon, UserPlus } from "lucide-react";

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // State to manage expanded menus by their labels
  const [openMenu, setOpenMenu] = useState(""); // For Level 1 ( reports, admission_form)
  const [openSubMenu, setOpenSubMenu] = useState(""); // For Level 2 ( attendance_reports)

  // Get inbox data from context to access unread counts
  const { totalUnreadCount } = useInboxContext();

  const routeToLabelMap = {
    [ADMIN_DASHBOARD_ROUTE]: "home",
    [ADMIN_STAFF_ROUTE]: "staff",
    [ADMIN_STUDENTS_ROUTE]: "students",
    [ADMIN_ROOMS_ROUTE]: "rooms",
    [ADMIN_QURAN_REPORT_ROUTE]: "quran_report",
    [ADMIN_STUDENT_ATTENDANCE_ROUTE]: "student_attendance_report",
    [ADMIN_STAFF_ATTENDANCE_ROUTE]: "staff_attendance_report",
    [ADMIN_MESSAGES_ROUTE]: "messages",
    [ADMIN_CALENDAR_ROUTE]: "calendar",
    [ADMIN_ADMISSION_FORM_ROUTE]: "admission_form",
    [ADMIN_ADMISSION_FORM_OVERVIEW_ROUTE]: "admission_overview",
    [ADMIN_DOCUMENTS_ROUTE]: "documents",
  };

  const currentPath = location.pathname + location.hash;
  // Try exact match first, then fall back to startsWith
  let activeItem = "home";
  // First, check for exact matches
  const exactMatch = Object.entries(routeToLabelMap).find(([route]) => 
    currentPath === route
  );
  if (exactMatch) {
    activeItem = exactMatch[1];
  } else {
    // If no exact match, use startsWith with sorted routes (longest first)
    const sortedRoutes = Object.entries(routeToLabelMap).sort(([routeA], [routeB]) => 
      routeB.length - routeA.length
    );
    const startsWithMatch = sortedRoutes.find(([route]) =>
      currentPath.startsWith(route)
    );
    if (startsWithMatch) {
      activeItem = startsWithMatch[1];
    }
  }

  const items = [
    {
      label: "home",
      icon: <HomeIcon width={20} />,
      route: ADMIN_DASHBOARD_ROUTE,
    },
    { label: "staff", icon: <StaffIcon width={20} />, route: ADMIN_STAFF_ROUTE },
    {
      label: "students",
      icon: <StudentIcon width={20} />,
      route: ADMIN_STUDENTS_ROUTE,
    },
    { label: "rooms", icon: <RoomIcon width={20} />, route: ADMIN_ROOMS_ROUTE },
    {
      label: "reports",
      icon: <ActivityReportIcon width={20} />,
      children: [
        {
          label: "student_attendance_report",
          icon: <AttendanceIcon width={20} />,
          route: ADMIN_STUDENT_ATTENDANCE_ROUTE,
        },
        { 
          label: "staff_attendance_report",
          icon: <AttendanceIcon width={20} />,
          route: ADMIN_STAFF_ATTENDANCE_ROUTE,
        },
        {
          label: "quran_report",
          icon: <QuranBlackAndWhiteIcon width={20} />,
          route: ADMIN_QURAN_REPORT_ROUTE,
        },
      ],
    },
    { 
      label: "messages", 
      icon: <MessagesIcon width={20} />, 
      route: ADMIN_MESSAGES_ROUTE,
    },
    { 
      label: "calendar", 
      icon: <CalendarIcon width={20} />, 
      route: ADMIN_CALENDAR_ROUTE,
    },
    
    {
      label: "admission_forms",
      icon: <UserPlus width={18} />,
      children: [
        {
        label: "admission_form", 
        icon: <LinkIcon size={20} />, 
        route: ADMIN_ADMISSION_FORM_ROUTE 
        },
        {
          label: "admission_overview",
          icon: <FolderCheckIcon size={20} />,
          route: ADMIN_ADMISSION_FORM_OVERVIEW_ROUTE,
        },
      ],
    },
    {
      label: "documents",
      icon: <BookIcon size={20} />,
      route: ADMIN_DOCUMENTS_ROUTE,
    }
  ];

  const isRTL = i18n.dir() === "rtl";

  return (
    <aside
      className={clsx(
        styles.sideBar,
        collapsed ? styles.collapsed : styles.expanded
      )}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className={`${styles.headerSection} ${collapsed && "p-0"}`}>
        {!collapsed && (
          <div className={styles.logoSection}>
            <Logo width={150} />
          </div>
        )}
        <div className={styles.toggleButtonWrapper}>
          <button
            className={styles.toggleButton}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? t("Expand sidebar") : t("Collapse sidebar")}
            title={collapsed ? t("Expand sidebar") : t("Collapse sidebar")}
          >
            {collapsed ? <FiMenu size={20} /> : <FiChevronLeft size={20} />}
          </button>
        </div>
      </div>

      <nav className={`py-4 ${styles.navLinks} ${!collapsed && styles.padd}`}>
        {items.map((item) => {
          if (item.children) {
            // Check if this specific Level 1 menu is open
            const isMenuOpen = openMenu === item.label;
            return (
              <div key={item.label}>
                {/* Level 1 Parent Button */}
                <button
                  onClick={() => setOpenMenu(isMenuOpen ? "" : item.label)}
                  className={clsx(
                    styles.sideBarItem,
                    activeItem === item.label
                      ? styles.activeButton
                      : styles.inactiveButton,
                    collapsed && styles.collapsedItem
                  )}
                >
                  <span className={styles.iconWrapper}>{item.icon}</span>
                  {!collapsed && (
                    <>
                      <span className={styles.label}>
                        {t(`admin_titles.${item.label}`)}
                      </span>
                      <span className={styles.chevron}>
                        {isMenuOpen ? <FiChevronDown /> : <FiChevronRight />}
                      </span>
                    </>
                  )}
                </button>

                {/* Sub-Menu Level 2 */}
                {isMenuOpen && !collapsed && (
                  <div
                    className={clsx(
                      styles.subMenu,
                      isRTL ? styles.subMenuRTL : styles.subMenuLTR
                    )}
                  >
                    {item.children.map((child) => (
                      <div key={child.label}>
                        {/* Check if this child has its own children (Level 3) */}
                        {child.children ? (
                          <div>
                            <button
                              onClick={() => setOpenSubMenu(openSubMenu === child.label ? "" : child.label)}
                              className={clsx(
                                styles.sideBarItem,
                                styles.subMenuItem,
                                activeItem === child.label
                                  ? styles.activeButton
                                  : styles.inactiveButton
                              )}
                            >
                              <span className={styles.iconWrapper}>{child.icon}</span>
                              <span className={styles.label}>
                                {t(`admin_titles.${child.label}`)}
                              </span>
                              <span className={styles.chevron}>
                                {openSubMenu ? <FiChevronDown /> : <FiChevronRight />}
                              </span>
                            </button>
                            {/* Level 3 Nested Menu Rendering */}
                            {openSubMenu && !collapsed && (
                              <div
                                className={clsx(
                                  styles.subMenu,
                                  isRTL ? styles.subMenuRTL : styles.subMenuLTR,
                                  styles.subSubMenu
                                )}
                              >
                                {child.children.map((subChild) => (
                                  <button
                                    key={subChild.label}
                                    onClick={() => navigate(subChild.route)}
                                    className={clsx(
                                      styles.sideBarItem,
                                      styles.subMenuItem,
                                      activeItem === subChild.label
                                        ? styles.activeButton
                                        : styles.inactiveButton
                                    )}
                                  >
                                    <span className={styles.iconWrapper}>{subChild.icon}</span>
                                    <span className={styles.label}>
                                      {t(`admin_pages.attendance_reports.report_types.${subChild.label}`)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => navigate(child.route)}
                            className={clsx(
                              styles.sideBarItem,
                              styles.subMenuItem,
                              activeItem === child.label
                                ? styles.activeButton
                                : styles.inactiveButton
                            )}
                          >
                            <span className={styles.iconWrapper}>{child.icon}</span>
                            <span className={styles.label}>
                              {t(`admin_titles.${child.label}`)}
                            </span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <button
              key={item.label}
              onClick={() => navigate(item.route)}
              className={clsx(
                styles.sideBarItem,
                activeItem === item.label
                  ? styles.activeButton
                  : styles.inactiveButton,
                collapsed && styles.collapsedItem
              )}
            >
              <span className={styles.iconWrapper}>{item.icon}</span>
              {!collapsed && (
                <span className={styles.label}>
                  {t(`admin_titles.${item.label}`)}
                </span>
              )}
              {!collapsed && item.label === "messages" && totalUnreadCount > 0 && (
                <span className={clsx(styles.messageBadge, activeItem === item.label
                  ? styles.messageActiveBadge
                  : null)}>
                  {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {!collapsed && (
        <div className={styles.footer}>
          <UserMenu />
        </div>
      )}
    </aside>
  );
};
